import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useTabs, markTabSwitchNav, getSearchFor } from '../context/TabsContext';
import { NAV_INDEX, routeForResult, TYPE_LABELS } from '../config/navSearchIndex';
import { CARD_INDEX } from '../config/cardSearchIndex';
import { Users, Wallet, FileText, CreditCard, Building2, Receipt, UserPlus, Compass, User, Clock, LayoutGrid, Pin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENTS_KEY = 'bbc:search-recents';

const TYPE_ICON = {
  navigation: Compass, customer: Users, application: FileText, loan: Wallet,
  payment: CreditCard, potential: UserPlus, expense: Receipt, staff: User,
  investor: Users, branch: Building2,
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [dbResults, setDbResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const { user } = useAuth();
  const { tabs, minimized, pinned, restoreTab } = useTabs();
  const navigate = useNavigate();
  const debounceRef = useRef();

  const loadRecents = useCallback(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      setRecents(raw ? JSON.parse(raw) : []);
    } catch { setRecents([]); }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('bbc:global-search-open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('bbc:global-search-open', onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) loadRecents();
    else { setQ(''); setDbResults([]); setLoading(false); setTypeFilter('all'); }
  }, [open, loadRecents]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) { setDbResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/search/global?q=${encodeURIComponent(term)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        setDbResults(res.ok ? (data.results || []) : []);
      } catch (e) {
        setDbResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const role = user?.role;
  const term = q.trim().toLowerCase();
  const matchNav = (list) => list.filter((n) => {
    if (role && role !== 'admin' && !n.roles.includes(role)) return false;
    const hay = `${n.label} ${n.keywords || ''}`.toLowerCase();
    return term.split(/\s+/).every((t) => hay.includes(t));
  });
  const navResults = term.length >= 1
    ? [...matchNav(NAV_INDEX), ...matchNav(CARD_INDEX)].slice(0, 10)
    : [];

  // Browser-address-bar-style tab jump: typing a few letters of an open
  // tab's title (or opening the palette empty) surfaces it here so staff
  // can switch without hunting through the tab strip.
  const tabResults = tabs.filter((t) => term.length < 1 || (t.title || '').toLowerCase().includes(term));
  const jumpToTab = useCallback((path) => {
    setOpen(false);
    if (minimized.includes(path)) restoreTab(path);
    markTabSwitchNav();
    navigate(path + getSearchFor(path), { replace: true });
  }, [minimized, restoreTab, navigate]);

  const go = useCallback((href, recentItem) => {
    if (recentItem) {
      try {
        const prev = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
        const next = [recentItem, ...prev.filter((r) => r.key !== recentItem.key)].slice(0, 8);
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
    }
    setOpen(false);
    navigate(href);
  }, [navigate]);

  const clearRecents = useCallback(() => {
    try { localStorage.removeItem(RECENTS_KEY); } catch { /* ignore */ }
    setRecents([]);
  }, []);

  const grouped = dbResults.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});
  const showNav = navResults.length > 0 && typeFilter === 'all';
  const showTabs = tabResults.length > 0 && typeFilter === 'all';
  const visibleGrouped = typeFilter === 'all'
    ? grouped
    : (grouped[typeFilter] ? { [typeFilter]: grouped[typeFilter] } : {});
  const hasVisible = (showNav ? navResults.length : 0) + (showTabs ? tabResults.length : 0) + Object.keys(visibleGrouped).length > 0;
  const TYPE_CHIPS = [['all', 'All'], ['customer', 'Customers'], ['loan', 'Loans'], ['payment', 'Payments']];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 max-w-2xl gap-0 [&>button]:text-red-600 [&>button:hover]:text-red-700 [&>button]:opacity-100">
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription className="sr-only">Search customers, loans, staff, records and app menus.</DialogDescription>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item][data-selected=true]]:!bg-forest-100 [&_[cmdk-item][data-selected=true]]:!text-forest-900">
          <CommandInput
            placeholder="Search customers, loans, staff, menus, open tabs…  (Ctrl / ⌘ K)"
            value={q}
            onValueChange={setQ}
            data-testid="global-search-input"
          />
          {term.length >= 1 && (
            <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap" data-testid="gs-type-chips">
              {TYPE_CHIPS.map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setTypeFilter(val)}
                  data-testid={`gs-chip-${val}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${typeFilter === val ? 'bg-forest-600 text-white border-forest-600' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <CommandList data-testid="global-search-results" className="max-h-[420px]">
            {term.length < 1 && recents.length > 0 && (
              <CommandGroup heading="Recent — quick jump">
                {recents.map((r) => {
                  const Icon = TYPE_ICON[r.type] || Compass;
                  return (
                    <CommandItem key={r.key} value={r.key} onSelect={() => go(r.href, r)} data-testid={`gs-recent-${r.key}`}>
                      <Icon className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{r.title}</span>
                        {r.subtitle && <span className="text-xs text-muted-foreground truncate">{r.subtitle}</span>}
                      </div>
                    </CommandItem>
                  );
                })}
                <CommandItem value="clear-recents" onSelect={clearRecents} data-testid="gs-clear-recents" className="justify-center text-xs text-muted-foreground">
                  <Clock className="w-3 h-3 mr-1" /> Clear recent
                </CommandItem>
              </CommandGroup>
            )}
            {term.length < 1 && recents.length === 0 && tabResults.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">Type to search across the whole app…</div>
            )}
            {term.length >= 1 && !hasVisible && !loading && (
              <CommandEmpty>No results for “{q}”.</CommandEmpty>
            )}

            {showTabs && (
              <CommandGroup heading="Open Tabs">
                {tabResults.map((t) => (
                  <CommandItem
                    key={`tab-${t.path}`}
                    value={`tab-${t.title}-${t.path}`}
                    onSelect={() => jumpToTab(t.path)}
                    data-testid={`gs-tab-${t.path}`}
                  >
                    {pinned.includes(t.path) ? <Pin className="w-4 h-4 mr-2 text-forest-500 shrink-0" /> : <LayoutGrid className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />}
                    <span className="truncate">{t.title}</span>
                    {minimized.includes(t.path) && <span className="ml-auto text-[10px] text-muted-foreground">Minimised</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {showNav && (
              <CommandGroup heading={TYPE_LABELS.navigation}>
                {navResults.map((n) => {
                  const Icon = TYPE_ICON.navigation;
                  return (
                    <CommandItem
                      key={`nav-${n.href}-${n.label}`}
                      value={`nav-${n.label}-${n.href}`}
                      onSelect={() => go(n.href, { key: `nav-${n.href}`, type: 'navigation', id: n.href, title: n.label, subtitle: '', href: n.href })}
                      data-testid={`gs-nav-${n.label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <Icon className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>{n.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {Object.entries(visibleGrouped).map(([type, items]) => {
              const Icon = TYPE_ICON[type] || Compass;
              return (
                <CommandGroup key={type} heading={TYPE_LABELS[type] || type}>
                  {items.map((r) => {
                    const href = r.href || routeForResult(type, r.id);
                    return (
                      <CommandItem
                        key={`${type}-${r.id}`}
                        value={`${type}-${r.id}`}
                        onSelect={() => go(href, { key: `${type}-${r.id}`, type, id: r.id, title: r.title, subtitle: r.subtitle, href })}
                        data-testid={`gs-${type}-${r.id}`}
                      >
                        <Icon className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{r.title}</span>
                          {r.subtitle && <span className="text-xs text-muted-foreground truncate">{r.subtitle}</span>}
                        </div>
                        {r.meta && <span className="ml-auto text-[10px] uppercase text-muted-foreground shrink-0">{r.meta}</span>}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}

            {loading && <div className="py-3 text-center text-xs text-muted-foreground">Searching…</div>}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
